import React, { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Text } from "@react-three/drei";
import * as THREE from "three";

function EnergyRing({ isSpeaking, isRecording, timeLeft=300, showTimer=true }) {
  const ringRef = useRef();
  const glowRef = useRef();

  // Format time for display (MM:SS)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useFrame(({ clock }) => {
    const elapsedTime = clock.getElapsedTime();

    // Pulsing effect
    const scale = 0.7 + Math.sin(elapsedTime * 3) * 0.05;
    ringRef.current.scale.set(scale, scale, scale);

    // Glow effect
    glowRef.current.material.opacity = Math.abs(Math.sin(elapsedTime * 2));

    let newColor;
    if (isSpeaking) {
      newColor = new THREE.Color("rgb(19,161,64)"); // Green
    } else if (isRecording) {
      newColor = new THREE.Color("#0cf"); // Blue-ish
    } else {
      newColor = new THREE.Color("orange"); // Thinking: orange
    }

    ringRef.current.material.color.lerp(newColor, 0.1);
    ringRef.current.material.emissive.lerp(newColor, 0.1);
  });

  return (
    <group position={[0, 0, 0]}> {/* Centered */}
      {/* Outer Glowing Ring */}
      <mesh ref={glowRef}>
        <torusGeometry args={[1.3, 0.09, 32, 100]} />
        <meshBasicMaterial color={"#3498db"} transparent opacity={0.6} />
      </mesh>

      {/* Main Energy Ring */}
      <mesh ref={ringRef}>
        <torusGeometry args={[1.2, 0.09, 32, 100]} />
        <meshStandardMaterial
          color={"#0cf"}
          emissive={"#0cf"}
          emissiveIntensity={1.5}
          roughness={0.2}
          metalness={0.8}
        />
      </mesh>

      {/* Central Timer Display */}
      {showTimer && (timeLeft >= 0) && (
        <Text position={[0,0,0]} color="white" fontSize={0.4} anchorX="center" anchorY="middle">
          {formatTime(timeLeft)}
        </Text>
      )}
    </group>
  );
}

export default function EnergyVisualizer({ isSpeaking, isRecording, timeLeft=300, showTimer=true }) {
  const getStatus = () => {
    if (isSpeaking) {
      return { className: "ispeaking-label" };
    } else if (isRecording) {
      return { className: "ilistening-label" };
    } else {
      return { className: "ithinking-label" };
    }
  };

  const status = getStatus();

  return (
    <div className="ivisualizer" style={{ background: "black", height: "80%" }}>
      <Canvas camera={{ position: [0, 0, 3], fov: 75 }}>
        <ambientLight intensity={0.5} />
        <pointLight position={[5, 5, 5]} intensity={2} />
        <EnergyRing isSpeaking={isSpeaking} isRecording={isRecording} timeLeft={timeLeft} showTimer={showTimer} />
        <OrbitControls enableRotate={false} enablePan={false} enableZoom={false} />
      </Canvas>
    </div>
  );
}